import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  getSubmissionStatus,
  getSubmissionTeamDetails,
  getAllUserSubmissions,
  getUserSubmissions,
  getSubmissionWeeks,
  getSubmissionYears,
  getRecentSubmissions,
  getSubmissionHealth,
  getUserSubmissionHistory,
  sendSubmissionNudge,
  type SubmissionTeamRow,
  type SubmissionUserRow,
  type SubmissionStatus,
  type NudgeResultResponse,
} from '../../api/adminClient';

// ============================================================================
// WEEK HELPERS (mirrors Delivery Monitor patterns)
// ============================================================================

function getCurrentWeek(): { week: number; year: number } {
  const now = new Date();
  const dayOfWeek = now.getUTCDay();
  const hours = now.getUTCHours();
  let daysSinceWeekStart = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  if (dayOfWeek === 1 && hours < 8) daysSinceWeekStart = 7;
  const weekStart = new Date(now);
  weekStart.setUTCDate(now.getUTCDate() - daysSinceWeekStart);
  weekStart.setUTCHours(8, 0, 0, 0);
  const jan1 = new Date(Date.UTC(weekStart.getUTCFullYear(), 0, 1));
  const days = Math.floor((weekStart.getTime() - jan1.getTime()) / (24 * 60 * 60 * 1000));
  const weekNumber = Math.ceil((days + jan1.getUTCDay() + 1) / 7);
  return { week: weekNumber, year: weekStart.getUTCFullYear() };
}

function getWeekStartDate(week: number, year: number): Date {
  const jan4 = new Date(year, 0, 4);
  const jan4DayOfWeek = jan4.getDay() || 7;
  const week1Monday = new Date(jan4);
  week1Monday.setDate(jan4.getDate() - (jan4DayOfWeek - 1));
  const targetMonday = new Date(week1Monday);
  targetMonday.setDate(week1Monday.getDate() + (week - 1) * 7);
  return targetMonday;
}

interface WeekInfo {
  week: number;
  start: Date;
  end: Date;
  label: string;
}

function getWeeksForMonth(year: number, month: number): WeekInfo[] {
  const weeks: WeekInfo[] = [];
  for (let w = 1; w <= 53; w++) {
    const start = getWeekStartDate(w, year);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    if (start.getMonth() === month && start.getFullYear() === year) {
      const label = `W${w}: ${start.getMonth() + 1}/${start.getDate()}–${end.getMonth() + 1}/${end.getDate()}`;
      weeks.push({ week: w, start, end, label });
    }
  }
  return weeks;
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

// ============================================================================
// PAGE
// ============================================================================

export default function AdminSubmissions() {
  const queryClient = useQueryClient();
  const current = useMemo(() => getCurrentWeek(), []);
  const currentMonth = new Date().getMonth();
  const [selectedYear, setSelectedYear] = useState(current.year);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedWeek, setSelectedWeek] = useState(current.week);

  const [expandedTeam, setExpandedTeam] = useState<{ teamId: string | null; status: SubmissionStatus | null } | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const [pendingOnly, setPendingOnly] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [scopeFilter, setScopeFilter] = useState<'all' | 'customer' | 'product'>('all');
  const [teamFilter, setTeamFilter] = useState<Set<string>>(new Set());

  const [nudgeConfirm, setNudgeConfirm] = useState<{ userIds: string[]; label: string } | null>(null);
  const [nudgeResult, setNudgeResult] = useState<NudgeResultResponse | null>(null);

  // ----- Queries -----
  const { data: years } = useQuery({ queryKey: ['submission-years'], queryFn: getSubmissionYears });

  const { data: weeksData } = useQuery({
    queryKey: ['submission-weeks', selectedYear],
    queryFn: () => getSubmissionWeeks(selectedYear),
  });
  const weeksSet = useMemo(() => new Set(weeksData?.weeks || []), [weeksData]);
  const firstYear = weeksData?.first_year ?? current.year;
  const firstWeek = weeksData?.first_week ?? 1;

  const { data: statusData, isLoading: statusLoading } = useQuery({
    queryKey: ['submission-status', selectedYear, selectedWeek],
    queryFn: () => getSubmissionStatus(selectedYear, selectedWeek),
  });

  const { data: teamUsers, isLoading: teamUsersLoading } = useQuery({
    queryKey: ['submission-team', selectedYear, selectedWeek, expandedTeam?.teamId, expandedTeam?.status],
    queryFn: () =>
      getSubmissionTeamDetails(
        selectedYear,
        selectedWeek,
        expandedTeam!.teamId,
        expandedTeam!.status || undefined,
      ),
    enabled: !!expandedTeam,
  });

  // Global-search mode: when the user types in search OR picks a non-"all" scope,
  // collapse the team table and show a flat cross-team user list.
  const globalSearchActive = userSearch.trim().length > 0 || scopeFilter !== 'all';
  const { data: allUsers, isLoading: allUsersLoading } = useQuery({
    queryKey: ['submission-all-users', selectedYear, selectedWeek],
    queryFn: () => getAllUserSubmissions(selectedYear, selectedWeek),
    enabled: globalSearchActive,
  });

  const { data: userReports, isLoading: userReportsLoading } = useQuery({
    queryKey: ['submission-user', selectedYear, selectedWeek, selectedUserId],
    queryFn: () => getUserSubmissions(selectedYear, selectedWeek, selectedUserId!),
    enabled: !!selectedUserId,
  });

  const { data: userHistory } = useQuery({
    queryKey: ['submission-user-history', selectedUserId],
    queryFn: () => getUserSubmissionHistory(selectedUserId!, 8),
    enabled: !!selectedUserId,
  });

  const { data: recent } = useQuery({
    queryKey: ['submission-recent'],
    queryFn: () => getRecentSubmissions(10),
    refetchInterval: 30000,
  });

  const { data: health } = useQuery({
    queryKey: ['submission-health'],
    queryFn: getSubmissionHealth,
    refetchInterval: 60000,
  });

  const nudgeMutation = useMutation({
    mutationFn: (userIds: string[]) =>
      sendSubmissionNudge(userIds, { year: selectedYear, week: selectedWeek }),
    onSuccess: (result) => {
      setNudgeResult(result);
      setNudgeConfirm(null);
      queryClient.invalidateQueries({ queryKey: ['submission-status'] });
      queryClient.invalidateQueries({ queryKey: ['submission-team'] });
    },
  });

  // ----- Auto-select on year/month change -----
  const monthWeeks = useMemo(() => getWeeksForMonth(selectedYear, selectedMonth), [selectedYear, selectedMonth]);

  useEffect(() => {
    if (selectedYear === current.year) setSelectedMonth(currentMonth);
    else setSelectedMonth(0);
    setExpandedTeam(null);
    setSelectedUserId(null);
  }, [selectedYear]);

  useEffect(() => {
    const weeks = getWeeksForMonth(selectedYear, selectedMonth);
    if (weeks.length === 0) return;
    const currentInMonth = weeks.find((w) => w.week === current.week && selectedYear === current.year);
    if (currentInMonth) setSelectedWeek(currentInMonth.week);
    else {
      const withData = weeks.filter((w) => weeksSet.has(w.week));
      setSelectedWeek(withData.length > 0 ? withData[withData.length - 1].week : weeks[weeks.length - 1].week);
    }
    setExpandedTeam(null);
    setSelectedUserId(null);
  }, [selectedMonth, selectedYear]);

  // ----- Helpers -----
  const isMonthDisabled = (month: number) => {
    const now = new Date();
    if (selectedYear === now.getFullYear() && month > now.getMonth()) return true;
    if (selectedYear > now.getFullYear()) return true;
    if (selectedYear === firstYear) {
      const firstWeekStart = getWeekStartDate(firstWeek, firstYear);
      if (month < firstWeekStart.getMonth()) return true;
    }
    if (selectedYear < firstYear) return true;
    return false;
  };
  const isWeekFuture = (w: WeekInfo) => w.start > new Date();
  const isWeekBeforeFirstData = (w: WeekInfo) =>
    selectedYear < firstYear || (selectedYear === firstYear && w.week < firstWeek);

  const summary = statusData?.summary || { eligible: 0, submitted: 0, pending: 0, overdue: 0, coverage_pct: 0 };
  const teams: SubmissionTeamRow[] = useMemo(() => {
    let rows = statusData?.teams || [];
    if (pendingOnly) rows = rows.filter((t) => t.pending + t.overdue > 0);
    if (teamFilter.size > 0) rows = rows.filter((t) => teamFilter.has(t.team_id ?? '__no_team__'));
    return rows;
  }, [statusData, pendingOnly, teamFilter]);

  // Apply user-level filters within expanded team
  const filteredTeamUsers = useMemo(() => {
    if (!teamUsers) return teamUsers;
    let rows = teamUsers;
    if (pendingOnly) rows = rows.filter((u) => u.status !== 'submitted');
    return rows;
  }, [teamUsers, pendingOnly]);

  // Flat global user list used when search or scope filter is active
  const filteredGlobalUsers = useMemo(() => {
    if (!allUsers) return undefined;
    let rows = allUsers;
    if (userSearch.trim()) {
      const q = userSearch.trim().toLowerCase();
      rows = rows.filter((u) => u.user_name.toLowerCase().includes(q));
    }
    if (scopeFilter !== 'all') rows = rows.filter((u) => u.scope_type === scopeFilter);
    if (pendingOnly) rows = rows.filter((u) => u.status !== 'submitted');
    if (teamFilter.size > 0) {
      rows = rows.filter((u) => teamFilter.has(u.team_id ?? '__no_team__'));
    }
    return rows;
  }, [allUsers, userSearch, scopeFilter, pendingOnly, teamFilter]);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Submission Monitor</h1>
        <p className="text-gray-500 mt-1">Track whether users have submitted their weekly reports</p>
      </div>

      {/* Anomaly Banner */}
      {health && health.anomaly !== 'normal' && (
        <div
          className={clsx(
            'mb-4 rounded-xl border p-4 flex items-center justify-between',
            health.anomaly === 'silent'
              ? 'bg-red-50 border-red-200 text-red-800'
              : 'bg-yellow-50 border-yellow-200 text-yellow-800',
          )}
        >
          <div>
            <p className="font-semibold">
              {health.anomaly === 'silent' ? '🔇 No submissions today' : '⚠️ Submission volume is low'}
            </p>
            <p className="text-sm mt-1">
              Today: <strong>{health.today_count}</strong> · 4-week avg (same day):{' '}
              <strong>{health.avg_last_4_weeks_same_day}</strong> · Threshold: {health.threshold_pct}%
            </p>
          </div>
        </div>
      )}

      {/* Pulse-Check Ticker */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-gray-700">Recent submissions</h2>
          <span className="text-xs text-gray-400">auto-refreshes every 30s</span>
        </div>
        {!recent || recent.length === 0 ? (
          <p className="text-sm text-gray-400">No submissions yet.</p>
        ) : (
          <ul className="divide-y divide-gray-50">
            {recent.map((r, idx) => (
              <li key={`${r.user_id}-${r.created_at}-${idx}`} className="py-2 flex items-center justify-between text-sm">
                <div className="min-w-0">
                  <span className="font-medium text-gray-900">{r.user_name}</span>
                  <span className="text-gray-500"> · {r.team_name || 'No Team'}</span>
                  <span className="text-gray-500"> → </span>
                  <span className="text-gray-700">{r.customer_name}</span>
                  {r.product_names.length > 0 && (
                    <span className="text-gray-400"> ({r.product_names.join(', ')})</span>
                  )}
                  <span className="text-gray-400"> · {r.engagement_type}</span>
                </div>
                <span className="text-xs text-gray-400 shrink-0 ml-3">{timeAgo(r.created_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Week Picker */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-4">
        <div className="flex items-center gap-4 mb-3">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          >
            {(years || [current.year]).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <div className="flex gap-1 flex-wrap">
            {MONTH_NAMES.map((name, i) => {
              const disabled = isMonthDisabled(i);
              const isActive = i === selectedMonth;
              return (
                <button
                  key={i}
                  onClick={() => !disabled && setSelectedMonth(i)}
                  disabled={disabled}
                  className={clsx(
                    'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                    isActive
                      ? 'bg-primary-600 text-white'
                      : disabled
                        ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100',
                  )}
                >
                  {name}
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {monthWeeks
            .filter((w) => !isWeekBeforeFirstData(w))
            .map((w) => {
              const isCurrent = w.week === current.week && selectedYear === current.year;
              const hasData = weeksSet.has(w.week);
              const isSelected = w.week === selectedWeek;
              const future = isWeekFuture(w);
              return (
                <button
                  key={w.week}
                  onClick={() => {
                    if (!future) {
                      setSelectedWeek(w.week);
                      setExpandedTeam(null);
                      setSelectedUserId(null);
                    }
                  }}
                  disabled={future}
                  className={clsx(
                    'relative px-3 py-2 text-xs rounded-lg font-medium transition-colors',
                    future
                      ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
                      : isSelected
                        ? 'bg-primary-600 text-white'
                        : isCurrent
                          ? 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                          : 'bg-gray-50 text-gray-600 hover:bg-gray-100',
                  )}
                >
                  {w.label}
                  {hasData && !isSelected && (
                    <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-primary-500" />
                  )}
                </button>
              );
            })}
          {monthWeeks.filter((w) => !isWeekBeforeFirstData(w)).length === 0 && (
            <span className="text-sm text-gray-400">No weeks in this month</span>
          )}
        </div>
      </div>

      {/* Filters Row */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-4 flex flex-wrap items-center gap-3 text-sm">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={pendingOnly}
            onChange={(e) => setPendingOnly(e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-gray-700">Not yet submitted only</span>
        </label>
        <input
          type="text"
          placeholder="Search user…"
          value={userSearch}
          onChange={(e) => setUserSearch(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none w-48"
        />
        <select
          value={scopeFilter}
          onChange={(e) => setScopeFilter(e.target.value as 'all' | 'customer' | 'product')}
          className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
        >
          <option value="all">All scopes</option>
          <option value="customer">Customer-scoped</option>
          <option value="product">Product-scoped</option>
        </select>
        {(statusData?.teams || []).length > 0 && (
          <details className="relative">
            <summary className="cursor-pointer px-3 py-1.5 border border-gray-300 rounded-lg text-gray-700">
              Teams ({teamFilter.size || 'all'})
            </summary>
            <div className="absolute z-10 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 min-w-[200px] max-h-72 overflow-y-auto">
              {statusData?.teams.map((t) => {
                const key = t.team_id ?? '__no_team__';
                const checked = teamFilter.has(key);
                return (
                  <label key={key} className="flex items-center gap-2 py-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const next = new Set(teamFilter);
                        if (checked) next.delete(key);
                        else next.add(key);
                        setTeamFilter(next);
                      }}
                      className="rounded"
                    />
                    <span className="text-sm text-gray-700">{t.team_name}</span>
                  </label>
                );
              })}
              {teamFilter.size > 0 && (
                <button
                  onClick={() => setTeamFilter(new Set())}
                  className="mt-2 text-xs text-primary-600 hover:underline"
                >
                  Clear
                </button>
              )}
            </div>
          </details>
        )}
        {statusData?.past_cutoff && (
          <span className="ml-auto text-xs text-gray-500 italic">
            Cutoff passed: {new Date(statusData.week_cutoff).toLocaleString()}
          </span>
        )}
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-5 gap-4 mb-4">
        <StatCard label="Eligible" value={summary.eligible} color="text-gray-900" />
        <StatCard label="Submitted" value={summary.submitted} color="text-green-600" />
        <StatCard label="Pending" value={summary.pending} color="text-yellow-600" />
        <StatCard label="Overdue" value={summary.overdue} color="text-red-600" />
        <StatCard label="Coverage" value={`${summary.coverage_pct}%`} color="text-primary-600" />
      </div>

      {/* Global Search Results — replaces team table when search/scope is active */}
      {globalSearchActive ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-4">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              W{selectedWeek}-{selectedYear} — matching users
            </h2>
            <span className="text-xs text-gray-500">
              {filteredGlobalUsers?.length ?? 0} user{(filteredGlobalUsers?.length ?? 0) === 1 ? '' : 's'} match across all teams
            </span>
          </div>
          {allUsersLoading ? (
            <div className="p-6 text-center text-gray-500">Loading…</div>
          ) : !filteredGlobalUsers || filteredGlobalUsers.length === 0 ? (
            <div className="p-6 text-center text-gray-500">No users match the current filters.</div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 text-left text-sm text-gray-500">
                  <th className="px-4 py-3 font-medium">User</th>
                  <th className="px-4 py-3 font-medium">Team</th>
                  <th className="px-4 py-3 font-medium">Scope</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium text-center">Submissions</th>
                  <th className="px-4 py-3 font-medium">Last Submitted</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredGlobalUsers.map((u) => (
                  <tr
                    key={u.user_id}
                    className={clsx(selectedUserId === u.user_id ? 'bg-primary-50' : 'hover:bg-gray-50')}
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{u.user_name}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{u.team_name || 'No Team'}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{u.scope_type || '—'}</td>
                    <td className="px-4 py-3"><StatusPill status={u.status} /></td>
                    <td className="px-4 py-3 text-center text-sm text-gray-700">{u.submission_count}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {u.last_submitted_at ? new Date(u.last_submitted_at).toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {u.submission_count > 0 && (
                          <button
                            onClick={() => setSelectedUserId(selectedUserId === u.user_id ? null : u.user_id)}
                            className={clsx(
                              'text-xs px-2 py-1 rounded',
                              selectedUserId === u.user_id
                                ? 'bg-primary-600 text-white'
                                : 'text-primary-700 hover:bg-primary-100',
                            )}
                          >
                            {selectedUserId === u.user_id ? 'Hide' : 'View'}
                          </button>
                        )}
                        {u.status !== 'submitted' && (
                          <button
                            onClick={() =>
                              setNudgeConfirm({ userIds: [u.user_id], label: `Nudge ${u.user_name}?` })
                            }
                            className="text-xs px-2 py-1 rounded text-primary-700 hover:bg-primary-100"
                          >
                            Nudge
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (
      /* Team Table */
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-4">
        <div className="p-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">W{selectedWeek}-{selectedYear} by Team</h2>
        </div>
        {statusLoading ? (
          <div className="p-6 text-center text-gray-500">Loading…</div>
        ) : teams.length === 0 ? (
          <div className="p-6 text-center text-gray-500">No teams match the current filters.</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 text-left text-sm text-gray-500">
                <th className="px-4 py-3 font-medium">Team</th>
                <th className="px-4 py-3 font-medium text-center">Eligible</th>
                <th className="px-4 py-3 font-medium text-center">Submitted</th>
                <th className="px-4 py-3 font-medium text-center">Pending</th>
                <th className="px-4 py-3 font-medium text-center">Overdue</th>
                <th className="px-4 py-3 font-medium text-center">Coverage</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {teams.map((team) => {
                const teamKey = team.team_id ?? '__no_team__';
                const isExpanded = expandedTeam?.teamId === team.team_id;
                return (
                  <TeamRow
                    key={teamKey}
                    team={team}
                    isExpanded={isExpanded}
                    expandedStatus={isExpanded ? expandedTeam!.status : null}
                    onCellClick={(teamId, status, count) => {
                      if (count === 0) return;
                      if (
                        expandedTeam?.teamId === teamId &&
                        expandedTeam?.status === status
                      ) {
                        setExpandedTeam(null);
                        setSelectedUserId(null);
                      } else {
                        setExpandedTeam({ teamId, status });
                        setSelectedUserId(null);
                      }
                    }}
                    teamUsers={isExpanded ? filteredTeamUsers : undefined}
                    teamUsersLoading={isExpanded && teamUsersLoading}
                    selectedUserId={selectedUserId}
                    onUserClick={(uid) => setSelectedUserId(selectedUserId === uid ? null : uid)}
                    onNudgeTeam={() => {
                      const pendingIds = (filteredTeamUsers || teamUsers || [])
                        .filter((u) => u.status !== 'submitted')
                        .map((u) => u.user_id);
                      if (pendingIds.length === 0) return;
                      setNudgeConfirm({
                        userIds: pendingIds,
                        label: `Nudge ${pendingIds.length} pending user${pendingIds.length === 1 ? '' : 's'} in ${team.team_name}?`,
                      });
                    }}
                    onNudgeUser={(uid, name) =>
                      setNudgeConfirm({ userIds: [uid], label: `Nudge ${name}?` })
                    }
                  />
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      )}

      {/* User submission drill */}
      {selectedUserId && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {(teamUsers?.find((u) => u.user_id === selectedUserId)?.user_name)
                  || (allUsers?.find((u) => u.user_id === selectedUserId)?.user_name)
                  || 'User'} — submissions this week
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {userReports?.length ?? 0} report{(userReports?.length ?? 0) === 1 ? '' : 's'} in W{selectedWeek}-{selectedYear}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {userHistory && userHistory.length > 0 && (
                <span className="text-xs text-gray-500">
                  Last 8 weeks: {userHistory.map((h) => `W${h.week}:${h.submission_count}`).join(' · ')}
                </span>
              )}
              <button
                onClick={() => setSelectedUserId(null)}
                className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1"
                title="Close"
              >
                ✕
              </button>
            </div>
          </div>
          {userReportsLoading ? (
            <div className="text-center text-gray-500">Loading…</div>
          ) : !userReports || userReports.length === 0 ? (
            <p className="text-sm text-gray-500">No submissions for this week.</p>
          ) : (
            <ul className="space-y-3">
              {userReports.map((r) => (
                <li key={r.report_id} className="rounded-lg border border-gray-200 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm">
                      <span className="font-medium text-gray-900">{r.customer_name}</span>
                      {r.product_names.length > 0 && (
                        <span className="text-gray-500"> · {r.product_names.join(', ')}</span>
                      )}
                      <span className="text-gray-400"> · {r.engagement_type}</span>
                      <SentimentBadge sentiment={r.sentiment} />
                    </div>
                    <span className="text-xs text-gray-400">{new Date(r.created_at).toLocaleString()}</span>
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{r.notes}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Nudge confirm modal */}
      {nudgeConfirm && (
        <Modal onClose={() => setNudgeConfirm(null)}>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirm nudge</h3>
          <p className="text-sm text-gray-600 mb-4">{nudgeConfirm.label}</p>
          <p className="text-xs text-gray-500 mb-4">
            Each user receives a friendly Slack DM reminder. Already-nudged users (within this ISO week) are skipped automatically.
          </p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setNudgeConfirm(null)}
              className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={() => nudgeMutation.mutate(nudgeConfirm.userIds)}
              disabled={nudgeMutation.isPending}
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {nudgeMutation.isPending ? 'Sending…' : 'Send nudge'}
            </button>
          </div>
        </Modal>
      )}

      {/* Nudge result modal */}
      {nudgeResult && (
        <Modal onClose={() => setNudgeResult(null)}>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Nudge results</h3>
          <div className="space-y-3 text-sm">
            <ResultGroup
              title={`Nudged (${nudgeResult.nudged.length})`}
              color="text-green-700"
              items={nudgeResult.nudged.map((n) => n.user_name)}
            />
            {nudgeResult.skipped.length > 0 && (
              <ResultGroup
                title={`Skipped (${nudgeResult.skipped.length})`}
                color="text-yellow-700"
                items={nudgeResult.skipped.map((s) => `${s.user_name} — ${s.reason}`)}
              />
            )}
            {nudgeResult.failed.length > 0 && (
              <ResultGroup
                title={`Failed (${nudgeResult.failed.length})`}
                color="text-red-700"
                items={nudgeResult.failed.map((f) => `${f.user_name} — ${f.error}`)}
              />
            )}
          </div>
          <div className="flex justify-end mt-4">
            <button
              onClick={() => setNudgeResult(null)}
              className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              Close
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ============================================================================
// SUBCOMPONENTS
// ============================================================================

function StatCard({ label, value, color }: { label: string; value: number | string; color: string }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
      <p className={clsx('text-3xl font-bold', color)}>{value}</p>
      <p className="text-sm text-gray-500">{label}</p>
    </div>
  );
}

function StatusPill({ status }: { status: SubmissionStatus }) {
  const styles: Record<SubmissionStatus, string> = {
    submitted: 'bg-green-100 text-green-800',
    pending: 'bg-yellow-100 text-yellow-800',
    overdue: 'bg-red-100 text-red-800',
  };
  return (
    <span className={clsx('inline-flex items-center px-2 py-0.5 rounded text-xs font-medium', styles[status])}>
      {status}
    </span>
  );
}

function SentimentBadge({ sentiment }: { sentiment: string }) {
  const styles: Record<string, string> = {
    positive: 'bg-green-50 text-green-700',
    neutral: 'bg-gray-50 text-gray-700',
    negative: 'bg-red-50 text-red-700',
  };
  return (
    <span className={clsx('ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs', styles[sentiment] || 'bg-gray-50 text-gray-700')}>
      {sentiment}
    </span>
  );
}

function TeamRow({
  team,
  isExpanded,
  expandedStatus,
  onCellClick,
  teamUsers,
  teamUsersLoading,
  selectedUserId,
  onUserClick,
  onNudgeTeam,
  onNudgeUser,
}: {
  team: SubmissionTeamRow;
  isExpanded: boolean;
  expandedStatus: SubmissionStatus | null;
  onCellClick: (teamId: string | null, status: SubmissionStatus, count: number) => void;
  teamUsers?: SubmissionUserRow[];
  teamUsersLoading: boolean;
  selectedUserId: string | null;
  onUserClick: (userId: string) => void;
  onNudgeTeam: () => void;
  onNudgeUser: (userId: string, name: string) => void;
}) {
  const cell = (status: SubmissionStatus, value: number, baseColor: string) => (
    <td className="px-4 py-3 text-center">
      <button
        disabled={value === 0}
        onClick={() => onCellClick(team.team_id, status, value)}
        className={clsx(
          'px-2 py-1 rounded text-sm font-semibold',
          value === 0 && 'text-gray-300 cursor-not-allowed',
          value > 0 && isExpanded && expandedStatus === status
            ? 'bg-primary-100 text-primary-800'
            : value > 0 && baseColor,
        )}
      >
        {value}
      </button>
    </td>
  );

  const pendingPlusOverdue = team.pending + team.overdue;

  return (
    <>
      <tr className={isExpanded ? 'bg-gray-50' : 'hover:bg-gray-50'}>
        <td className="px-4 py-3 font-medium text-gray-900">{team.team_name}</td>
        <td className="px-4 py-3 text-center text-gray-700">{team.eligible}</td>
        {cell('submitted', team.submitted, 'hover:bg-green-50 text-green-700')}
        {cell('pending', team.pending, 'hover:bg-yellow-50 text-yellow-700')}
        {cell('overdue', team.overdue, 'hover:bg-red-50 text-red-700')}
        <td className="px-4 py-3 text-center">
          <CoverageBar pct={team.coverage_pct} />
        </td>
        <td className="px-4 py-3 text-right">
          <button
            onClick={onNudgeTeam}
            disabled={pendingPlusOverdue === 0}
            className={clsx(
              'text-xs px-2 py-1 rounded',
              pendingPlusOverdue === 0
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-primary-700 hover:bg-primary-50',
            )}
            title={pendingPlusOverdue === 0 ? 'No pending users' : `Nudge ${pendingPlusOverdue} pending`}
          >
            Nudge {pendingPlusOverdue || ''}
          </button>
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={7} className="bg-gray-50 px-4 py-3">
            {teamUsersLoading ? (
              <div className="text-sm text-gray-500">Loading users…</div>
            ) : !teamUsers || teamUsers.length === 0 ? (
              <div className="text-sm text-gray-500">No users match.</div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="text-xs text-gray-500">
                    <th className="px-2 py-1 text-left font-medium">User</th>
                    <th className="px-2 py-1 text-left font-medium">Scope</th>
                    <th className="px-2 py-1 text-left font-medium">Status</th>
                    <th className="px-2 py-1 text-left font-medium">Submissions</th>
                    <th className="px-2 py-1 text-left font-medium">Last Submitted</th>
                    <th className="px-2 py-1 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {teamUsers.map((u) => (
                    <tr
                      key={u.user_id}
                      className={clsx(
                        'border-t border-gray-100',
                        selectedUserId === u.user_id ? 'bg-primary-50' : 'hover:bg-white',
                      )}
                    >
                      <td className="px-2 py-2">
                        <button
                          onClick={() => onUserClick(u.user_id)}
                          className="text-sm text-gray-900 hover:underline"
                        >
                          {u.user_name}
                        </button>
                      </td>
                      <td className="px-2 py-2 text-xs text-gray-500">{u.scope_type || '—'}</td>
                      <td className="px-2 py-2"><StatusPill status={u.status} /></td>
                      <td className="px-2 py-2 text-sm text-gray-700">{u.submission_count}</td>
                      <td className="px-2 py-2 text-xs text-gray-500">
                        {u.last_submitted_at ? new Date(u.last_submitted_at).toLocaleString() : '—'}
                      </td>
                      <td className="px-2 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {u.submission_count > 0 && (
                            <button
                              onClick={() => onUserClick(u.user_id)}
                              className={clsx(
                                'text-xs px-2 py-1 rounded',
                                selectedUserId === u.user_id
                                  ? 'bg-primary-600 text-white'
                                  : 'text-primary-700 hover:bg-primary-100',
                              )}
                            >
                              {selectedUserId === u.user_id ? 'Hide' : 'View'}
                            </button>
                          )}
                          {u.status !== 'submitted' && (
                            <button
                              onClick={() => onNudgeUser(u.user_id, u.user_name)}
                              className="text-xs px-2 py-1 rounded text-primary-700 hover:bg-primary-100"
                            >
                              Nudge
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function CoverageBar({ pct }: { pct: number }) {
  const color =
    pct >= 80 ? 'bg-green-500' : pct >= 50 ? 'bg-yellow-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 bg-gray-200 rounded-full h-2 overflow-hidden">
        <div className={clsx('h-full', color)} style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
      </div>
      <span className="text-xs text-gray-700 w-8 text-right">{pct}%</span>
    </div>
  );
}

function Modal({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

function ResultGroup({ title, color, items }: { title: string; color: string; items: string[] }) {
  return (
    <div>
      <p className={clsx('font-medium', color)}>{title}</p>
      {items.length > 0 && (
        <ul className="mt-1 text-xs text-gray-600 list-disc list-inside space-y-0.5 max-h-32 overflow-y-auto">
          {items.map((label, i) => (
            <li key={i}>{label}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
